import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

// Filters by "starts with" (case-insensitive) rather than cmdk's default
// fuzzy/contains match - typing "I" should only surface options that start
// with "I", not anything that merely contains one.
const startsWithFilter = (itemValue, search) =>
  itemValue.toLowerCase().startsWith(search.toLowerCase()) ? 1 : 0

export function Combobox({
  value,
  onChange,
  options,
  placeholder = "Select...",
  emptyText = "No results found.",
  allowCustomValue = false,
  clearable = false,
  className,
}) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const trimmedSearch = search.trim()
  const hasExactMatch = options.some(
    (option) => option.toLowerCase() === trimmedSearch.toLowerCase()
  )
  const showCreate = allowCustomValue && trimmedSearch && !hasExactMatch

  const select = (nextValue) => {
    onChange(nextValue)
    setOpen(false)
    setSearch("")
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command filter={startsWithFilter}>
          <CommandInput
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {!showCreate && options.length > 0 && <CommandEmpty>{emptyText}</CommandEmpty>}
            <CommandGroup>
              {clearable && value && (
                <CommandItem value="__clear__" onSelect={() => select('')}>
                  <X className="mr-2 h-4 w-4" />
                  Clear selection
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem key={option} value={option} onSelect={() => select(option)}>
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option}
                </CommandItem>
              ))}
              {showCreate && (
                <CommandItem value={trimmedSearch} onSelect={() => select(trimmedSearch)}>
                  Create "{trimmedSearch}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
